import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories } from '@/mocks/articles';
import { formatViews, formatDate } from '@/utils/format';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <main className="pt-16 min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该内容可能已被移除或链接失效</p>
          <Link to="/" className="whitespace-nowrap px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '蜜桃传媒',
      url: 'https://mitaomedia.com',
    },
    url: `https://mitaomedia.com/article/${article.id}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://mitaomedia.com/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        canonicalUrl={`https://mitaomedia.com/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        schemaJson={articleSchema}
      />

      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: category?.name || '分类', href: `/category/${article.category}` },
          { label: article.title },
        ]} />

        <article className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <header className="mb-8">
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-600 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i> {formatDate(article.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i> {formatViews(article.views)} 阅读
              </span>
            </div>
          </header>

          <div className="w-full aspect-video rounded-lg overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 蜜桃传媒`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              [&_h2]:font-heading [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
              [&_p]:text-foreground-700 [&_p]:mb-4 [&_p]:leading-loose"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">分享文章：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200 cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200 cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors duration-200 cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
              <Link
                to={`/category/${article.category}`}
                className="whitespace-nowrap text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer"
              >
                返回{category?.name || '列表'} <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <article key={rel.id} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer" data-product-shop>
                    <Link to={`/article/${rel.id}`} className="block">
                      <div className="w-full h-36 overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          title={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                          {rel.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                          <span>{formatDate(rel.date)}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i> {formatViews(rel.views)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}